import React, { useState } from "react";
import {
  useGetCallDispositionsQuery,
  useCreateCallDispositionMutation,
  useUpdateCallDispositionMutation,
  useDeleteCallDispositionMutation,
} from "@/store/rest_api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import type CallDisposition from "@/model/interface/CallDisposition";
import { CallDispositionAction } from "@/enum/CallDispositionAction";
import { CallDispositionOutcome } from "@/enum/CallDispositionOutcome";
import DispositionRow from "./DispositionRow";
import DispositionFormDialog, { type DispositionFormData } from "./DispositionFormDialog";

const emptyForm: DispositionFormData = {
  name: "",
  description: "",
  color: "#6B7280",
  requiresFollowup: false,
  action: CallDispositionAction.NONE,
  outcome: CallDispositionOutcome.NEUTRAL,
  dealStageId: null,
};

function toDispositionRequest(form: DispositionFormData): Partial<CallDisposition> {
  return {
    name: form.name,
    description: form.description,
    color: form.color,
    requiresFollowup: form.requiresFollowup,
    action: form.action,
    outcome: form.outcome,
    dealStage: form.dealStageId ? { id: form.dealStageId } : null,
  };
}

export default function CallDispositionsPage() {
  const { data: dispositions = [], isLoading } = useGetCallDispositionsQuery({
    all: true,
  });
  const [createDisposition] = useCreateCallDispositionMutation();
  const [updateDisposition] = useUpdateCallDispositionMutation();
  const [deleteDisposition] = useDeleteCallDispositionMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<DispositionFormData>(emptyForm);
  const [deleteTarget, setDeleteTarget] = useState<CallDisposition | null>(null);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);

  const activeDispositions = dispositions.filter((d) => d.isActive);
  const inactiveDispositions = dispositions.filter((d) => !d.isActive);

  const handleDragStart = (_e: React.DragEvent, id: string) => {
    setDragId(id);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (_e: React.DragEvent, targetId: string) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      return;
    }

    const list = [...activeDispositions];
    const fromIndex = list.findIndex((d) => d.id === dragId);
    const toIndex = list.findIndex((d) => d.id === targetId);
    if (fromIndex === -1 || toIndex === -1) {
      setDragId(null);
      return;
    }

    const [moved] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, moved);

    setDragId(null);

    try {
      await Promise.all(
        list.map((d, i) => updateDisposition({ id: d.id, data: { sortOrder: i + 1 } as any }).unwrap()),
      );
      toast.success("Order updated");
    } catch {
      toast.error("Failed to reorder");
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (d: CallDisposition) => {
    setEditingId(d.id);
    setForm({
      name: d.name,
      description: d.description ?? "",
      color: d.color,
      requiresFollowup: d.requiresFollowup,
      action: d.action ?? CallDispositionAction.NONE,
      outcome: d.outcome ?? CallDispositionOutcome.NEUTRAL,
      dealStageId: d.dealStage?.id ?? null,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        await updateDisposition({ id: editingId, data: toDispositionRequest(form) }).unwrap();
        toast.success("Disposition updated");
      } else {
        await createDisposition(toDispositionRequest(form)).unwrap();
        toast.success("Disposition created");
      }
      setDialogOpen(false);
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to save disposition");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteDisposition(deleteTarget.id).unwrap();
      toast.success("Disposition removed");
      setDeleteTarget(null);
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to delete disposition");
    }
  };

  const handleToggleActive = async (d: CallDisposition) => {
    try {
      await updateDisposition({
        id: d.id,
        data: { isActive: !d.isActive } as any,
      }).unwrap();
      toast.success(d.isActive ? "Disposition deactivated" : "Disposition activated");
    } catch (e: any) {
      toast.error(e?.detail ?? "Failed to update disposition");
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-muted-foreground text-sm">Manage call outcome labels for your organisation</p>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Add Disposition
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Active Dispositions</CardTitle>
          <CardDescription>
            {activeDispositions.length} disposition
            {activeDispositions.length !== 1 ? "s" : ""}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {activeDispositions.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4">No active dispositions.</p>
          ) : (
            <div className="divide-y divide-border">
              {activeDispositions.map((d) => (
                <DispositionRow
                  key={d.id}
                  disposition={d}
                  onEdit={() => openEdit(d)}
                  onDelete={() => setDeleteTarget(d)}
                  onToggleActive={() => handleToggleActive(d)}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {inactiveDispositions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Inactive Dispositions</CardTitle>
            <CardDescription>
              {inactiveDispositions.length} deactivated disposition
              {inactiveDispositions.length !== 1 ? "s" : ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y divide-border">
              {inactiveDispositions.map((d) => (
                <DispositionRow
                  key={d.id}
                  disposition={d}
                  onEdit={() => openEdit(d)}
                  onDelete={() => setDeleteTarget(d)}
                  onToggleActive={() => handleToggleActive(d)}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <DispositionFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        form={form}
        onFormChange={setForm}
        onSave={handleSave}
        saving={saving}
        isEdit={!!editingId}
      />

      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Disposition</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <span className="font-semibold">{deleteTarget?.name}</span>? Existing
              calls with this disposition will keep their label.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
